import { useCallback, useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import DateModal from 'components/UI/Modal/Date/DateModal'
import { useModalContext } from 'context/modalContext'
import { type Orientation } from 'helpers/media'
import { useReelNavigate, useReelUploadProgress } from 'services/reels'
import { type IPoll } from 'types/interfaces/ITypes'

export type ReelVideo = {
  id?: string
  path?: string
  s3_path: string
  orientation?: Orientation
  video_exif?: any
  isLocal?: boolean
  trim_start?: number | null
  trim_end?: number | null
}

export type ReelVideos = ReelVideo[]

export type ReelPhoto = {
  id?: string
  path: string
  orientation: Orientation
  exif_data?: Record<string, any> | null
  storage_path?: string
  thumb_path?: string | null
  original_orientation?: Orientation | string | null
}

export type ReelGoal = {
  title?: string
  description?: string
  amount?: number | string
  [key: string]: any
}

export type NewReelPayload = {
  body: string
  discovery: true
  categories: number[]
  groups: number[]
  show_feed: true
  photos: Array<{
    id: string
    path: string
    order: number
    orientation?: Orientation | string | null
    exif_data?: string | null
    thumb_path?: string | null
    text?: string
  }>
videos: Array<{
  id: string
  path: string
  order: number
  orientation?: Orientation | string | null
  video_exif?: any
  duration?: number | null
  thumb_path?: string | null
  trim_start?: number | null
  trim_end?: number | null
}>
  sounds: any[]
  tags: any[]
  price: null
  post_preview_type: null
  schedule_date: string | null
  shareOnTwitter: boolean
  poll?: IPoll | null
  goal?: ReelGoal | null
}

type InitializeReelPayload = Partial<{
  body: string
  photo: ReelPhoto
  videos: ReelVideos
  poll: IPoll
  goal: ReelGoal
  schedule_date: string
  groups: number[]
  categories: number[]
  tags: any[]
  text: {
    value: string
    x: number
    y: number
  }
}>
const formatDateForApi = (date: Date | null) => {
  if (!date) return null
  return date.toISOString()
}

const normalizeReelPhotoPath = (photo?: ReelPhoto) => {
  if (!photo) return ''

  return (
    photo.storage_path ||
    photo.path?.replace(/^https?:\/\/[^/]+\//, '') ||
    photo.path
  )
}

const formatReelPayload = ({
  body,
  image,
  video,
  pollData,
  goalData,
  scheduleDate,
  selectedGroups,
  selectedCategories,
  tags,
  textValue
}: {
  body: string
  image?: ReelPhoto
  video?: ReelVideos
  pollData: IPoll | null
  goalData: ReelGoal | null
  scheduleDate: Date | null
  selectedGroups: number[]
  selectedCategories: number[]
  tags: any[]
  textValue: string
}): NewReelPayload => {
const photos = image
  ? [
      {
        id: image.id!,
        path: normalizeReelPhotoPath(image),
        order: 1,
        orientation: image.original_orientation ?? image.orientation,
        exif_data: image.exif_data
          ? JSON.stringify(image.exif_data)
          : null,
        thumb_path: image.thumb_path ?? null,
        text: textValue.trim() || undefined
      }
    ]
  : []
const videos =
  video?.map((item, index) => ({
    id: item.id!,
    path: item.s3_path,
    order: index + 1,
    orientation: item.orientation ?? null,
    video_exif: item.video_exif ?? null,
    duration:
      typeof item.video_exif === 'string'
        ? null
        : item.video_exif?.duration ?? null,
    thumb_path:
      typeof item.video_exif === 'string'
        ? null
        : item.video_exif?.thumb_path ?? null,
    trim_start: item.trim_start ?? null,
    trim_end: item.trim_end ?? null
  })) ?? []

  return {
    body: body.trim(),
    
    discovery: true,
    show_feed: true,

    categories: selectedCategories,
    groups: selectedGroups,

    photos,
    videos,
    sounds: [],
    tags,

    price: null,
    post_preview_type: null,
    schedule_date: formatDateForApi(scheduleDate),
    shareOnTwitter: false,

    poll: pollData,
    goal: goalData
  }
}

export function useCreateReel() {
  const modalData = useModalContext()
  const { t } = useTranslation()

  const [body, setBody] = useState('')

  const [pollData, setPollData] = useState<IPoll | null>(null)
  const [addPollOpen, setAddPollOpen] = useState(false)

  const [goalData, setGoalData] = useState<ReelGoal | null>(null)
  const [addGoalOpen, setAddGoalOpen] = useState(false)

  const [isAddTextOpen, setIsAddTextOpen] = useState(false)
  const [textValue, setTextValue] = useState('')
  const [textPosition, setTextPosition] = useState<[number, number]>([
    50.0,
    50.0
  ])
  const [initialPosition, setInitialPos] = useState<[number, number]>([
    50.0,
    50.0
  ])
  const [initialText, setInitialText] = useState('')
  const [isTextDragged, setIsTextDragged] = useState(false)

  const [scheduleDate, setScheduleDate] = useState<Date | null>(null)

  const [image, setImage] = useState<ReelPhoto>()
  const [video, setVideo] = useState<ReelVideos>()

  const [selectedGroups, setSelectedGroups] = useState<number[]>([])
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])
  const [tags, setTags] = useState<any[]>([])

  const [mediaType, setMediaType] = useState<File | null>(null)

  useEffect(() => {
    const savedReel = localStorage.getItem('share-reel')

    if (!savedReel) return

    const parsedReel = JSON.parse(savedReel) as InitializeReelPayload

    initializeReel(parsedReel)
    localStorage.removeItem('share-reel')
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const schedule = params.get('schedule_date')

    if (schedule) {
      setScheduleDate(new Date(schedule))
    }
  }, [])

  const initializeReel = (newReel: InitializeReelPayload) => {
    if (!newReel) return

    if (newReel.body) {
      setBody(newReel.body)
    }

    if (newReel.photo) {
      setImage(newReel.photo)
    }

    if (newReel.videos?.length) {
      setVideo(newReel.videos)
    }

    if (newReel.text) {
      setTextPosition([newReel.text.x, newReel.text.y])
      setInitialPos([newReel.text.x, newReel.text.y])
      setTextValue(newReel.text.value)
      setInitialText(newReel.text.value)
      setIsAddTextOpen(true)
    }

    if (newReel.poll) {
      setPollData({
        ...newReel.poll,
        pollBg: '',
        pollBgImg: null
      })
    }

    if (newReel.goal) {
      setGoalData(newReel.goal)
    }

    if (newReel.schedule_date) {
      setScheduleDate(new Date(newReel.schedule_date))
    }

    if (newReel.groups) {
      setSelectedGroups(newReel.groups)
    }

    if (newReel.categories) {
      setSelectedCategories(newReel.categories)
    }

    if (newReel.tags) {
      setTags(newReel.tags)
    }
  }

  const handleRemoveText = useCallback(() => {
    setIsAddTextOpen(false)
    setTextPosition([50, 50])
    setInitialPos([50, 50])
    setTextValue('')
    setInitialText('')
  }, [])

  const handleSetCoordinates = useCallback(
    (x: number, y: number) => {
      if (x !== textPosition[0] || y !== textPosition[1]) {
        setTextPosition([x, y])
      }
    },
    [textPosition]
  )

  const handleOpenText = useCallback(() => {
    setIsAddTextOpen(true)
  }, [])

  const handleSetImageData = useCallback(
    (
      path: string,
      orientation: Orientation,
      exif_data?: any,
      storage_path?: string,
      thumb_path?: string | null,
      id?: string
    ) => {
      setImage({
        id,
        path,
        orientation,
        exif_data,
        storage_path,
        thumb_path
      })

      setVideo(undefined)
    },
    []
  )

  const handleSetVideoData = useCallback((videos: ReelVideos) => {
    setVideo(videos)
    setImage(undefined)

    // Remove text when switching to video
    setIsAddTextOpen(false)
    setTextPosition([50, 50])
    setInitialPos([50, 50])
    setTextValue('')
    setInitialText('')
  }, [])

  const clearMedia = useCallback(() => {
    if (video?.[0]?.s3_path?.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(video[0].s3_path)
      } catch {}
    }

    if (image?.path?.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(image.path)
      } catch {}
    }

    setImage(undefined)
    setVideo(undefined)
    setMediaType(null)
  }, [image, video])

  const { handleCloseClick, handleSettingsClick } = useReelNavigate()

  const { handleSetProgressInfo, uploadProgress, setUploadProgress } =
    useReelUploadProgress()

  const toggleDateModal = () => {
    modalData.addModal(
      t('schedulePost'),
      <DateModal
        confirmFn={setScheduleDate}
        newDateVal={scheduleDate ?? new Date()}
      />
    )
  }

  const handleSetPollFunction = (data: IPoll) => {
    setPollData({
      ...data,
      pollBg: 'transparent'
    })
  }

  const handleClearPollData = () => {
    setPollData(null)
  }

  const handleOpenPoll = () => {
    setAddPollOpen(true)
  }

  const handleSetGoalFunction = (data: ReelGoal) => {
    setGoalData(data)
  }

  const handleClearGoalData = () => {
    setGoalData(null)
  }

  const handleOpenGoal = () => {
    setAddGoalOpen(true)
  }

  const handleRemoveSchedule = () => {
    setScheduleDate(null)
  }

const handleUploadAbort = useCallback(() => {
  uploadProgress?.cancelCb?.()
  setUploadProgress(null)
  clearMedia()
}, [uploadProgress, setUploadProgress, clearMedia])

  const reelPayload = useMemo(
    () =>
      formatReelPayload({
        body,
        image,
        video,
        pollData,
        goalData,
        scheduleDate,
        selectedGroups,
        selectedCategories,
        tags,
        textValue
      }),
    [
      body,
      image,
      video,
      pollData,
      goalData,
      scheduleDate,
      selectedGroups,
      selectedCategories,
      tags,
      textValue
    ]
  )

const reelReady = useMemo(() => {
  if (image) {
    return Boolean(
      image.id &&
      image.storage_path
    )
  }

  if (video?.length) {
    return video.every((item) => {
      return Boolean(
        item.id &&
        item.s3_path &&
        !item.s3_path.startsWith('blob:')
      )
    })
  }

  return false
}, [image, video])

  return {
    body,
    setBody,

    image,
    video,
    mediaType,
    setMediaType,
    clearMedia,
    handleSetImageData,
    handleSetVideoData,

    isAddTextOpen,
    setIsAddTextOpen,
    initialPosition,
    initialText,
    textPosition,
    textValue,
    setTextValue,
    isTextDragged,
    setIsTextDragged,
    handleSetCoordinates,
    handleRemoveText,
    handleOpenText,

    pollData,
    addPollOpen,
    setAddPollOpen,
    setPollData,
    handleSetPollFunction,
    handleClearPollData,
    handleOpenPoll,

    goalData,
    addGoalOpen,
    setAddGoalOpen,
    setGoalData,
    handleSetGoalFunction,
    handleClearGoalData,
    handleOpenGoal,

    selectedGroups,
    setSelectedGroups,
    selectedCategories,
    setSelectedCategories,
    tags,
    setTags,

    scheduleDate,
    setScheduleDate,
    toggleDateModal,
    handleRemoveSchedule,

    reelPayload,
    reelReady,

    uploadProgress,
    handleSetProgressInfo,
    handleUploadAbort,

    handleSettingsClick,
    handleCloseClick
  }
}